import QrScanner from 'qr-scanner';

/**
 * Интерфейс для результата чтения QR-кода
 */
export interface QRReadResult {
  success: boolean;
  data?: string;
  error?: string;
}

/**
 * Утилита для чтения QR-кода из файла изображения
 * @param file - Файл изображения с QR-кодом
 * @returns Promise с результатом чтения QR-кода
 */
export const readQRFromFile = async (file: File): Promise<QRReadResult> => {
  try {
    // Проверяем, что файл является изображением
    if (!file.type.startsWith('image/')) {
      return {
        success: false,
        error: 'Файл должен быть изображением',
      };
    }

    // Проверяем размер файла (максимум 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'Размер файла не должен превышать 10MB',
      };
    }

    // Читаем QR-код из файла
    const result = await QrScanner.scanImage(file, {
      returnDetailedScanResult: true,
    });

    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    // Обрабатываем различные типы ошибок
    if (error instanceof Error) {
      if (error.message.includes('No QR code found')) {
        return {
          success: false,
          error: 'QR-код не найден в изображении',
        };
      }

      if (error.message.includes('Invalid image')) {
        return {
          success: false,
          error: 'Некорректное изображение',
        };
      }

      return {
        success: false,
        error: `Ошибка чтения QR-кода: ${error.message}`,
      };
    }

    return {
      success: false,
      error: 'Неизвестная ошибка при чтении QR-кода',
    };
  }
};

/**
 * Утилита для чтения QR-кода из URL изображения
 * @param imageUrl - URL изображения с QR-кодом
 * @returns Promise с результатом чтения QR-кода
 */
export const readQRFromUrl = async (imageUrl: string): Promise<QRReadResult> => {
  try {
    // Проверяем, что URL валидный
    if (!imageUrl || !imageUrl.startsWith('http')) {
      return {
        success: false,
        error: 'Некорректный URL изображения',
      };
    }

    // Читаем QR-код из URL
    const result = await QrScanner.scanImage(imageUrl, {
      returnDetailedScanResult: true,
    });

    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('No QR code found')) {
        return {
          success: false,
          error: 'QR-код не найден в изображении',
        };
      }

      if (error.message.includes('Failed to load image')) {
        return {
          success: false,
          error: 'Не удалось загрузить изображение',
        };
      }

      return {
        success: false,
        error: `Ошибка чтения QR-кода: ${error.message}`,
      };
    }

    return {
      success: false,
      error: 'Неизвестная ошибка при чтении QR-кода',
    };
  }
};

/**
 * Утилита для чтения QR-кода из canvas элемента
 * @param canvas - Canvas элемент с изображением QR-кода
 * @returns Promise с результатом чтения QR-кода
 */
export const readQRFromCanvas = async (
  canvas: HTMLCanvasElement,
): Promise<QRReadResult> => {
  try {
    // Читаем QR-код из canvas
    const result = await QrScanner.scanImage(canvas, {
      returnDetailedScanResult: true,
    });

    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('No QR code found')) {
        return {
          success: false,
          error: 'QR-код не найден в изображении',
        };
      }

      return {
        success: false,
        error: `Ошибка чтения QR-кода: ${error.message}`,
      };
    }

    return {
      success: false,
      error: 'Неизвестная ошибка при чтении QR-кода',
    };
  }
};
